import {
  Button,
  buttonVariants,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@xhubio-saas/core-frontend/ui-components'
import { CheckCircle2, RefreshCw, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  type CreatedDocument,
  type CreateFailure,
  OUTPUT_KINDS,
  type OutputKind,
} from '@/api/pdfExtractClient'

/** Die Vorlage „System-Standard" — ohne `templateId` nimmt der Server seine Standardvorlage. */
const DEFAULT_TEMPLATE = '__default__'

function base64ToBlob(base64: string, type: string): Blob {
  const raw = atob(base64)
  const bytes = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return new Blob([bytes], { type })
}

function decodeXml(base64: string): string {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}

/** Herunterladen unter dem Dateinamen der Antwort (sprechend, `UI_GUIDELINES` §31) — nie eine Kennung. */
function download(doc: CreatedDocument): void {
  const url = URL.createObjectURL(base64ToBlob(doc.data, doc.mimeType))
  const a = document.createElement('a')
  a.href = url
  a.download = doc.filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function Verdict({ doc }: { doc: CreatedDocument }) {
  const { t } = useTranslation()
  const status = doc.validation?.status ?? 'not_run'
  if (status === 'passed') {
    return (
      <p
        className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100"
        data-testid="invoice-from-pdf-verdict"
        data-status={status}
      >
        <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
        {t('invoiceFromPdf.create.valid')}
      </p>
    )
  }
  return (
    <div
      className="space-y-1 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
      data-testid="invoice-from-pdf-verdict"
      data-status={status}
    >
      <p className="flex items-center gap-2">
        <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
        {t(status === 'failed' ? 'invoiceFromPdf.create.invalid' : 'invoiceFromPdf.create.notRun')}
      </p>
      {(doc.validation?.errors ?? []).length > 0 && (
        <ul className="list-inside list-disc">
          {(doc.validation?.errors ?? []).map((e) => (
            <li key={`${e.code ?? ''}-${e.message}`}>
              {e.code ? `${e.code}: ` : ''}
              {e.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function FailureBlock({ failure }: { failure: CreateFailure }) {
  const { t } = useTranslation()
  if (failure.kind === 'quota') {
    return (
      <div
        className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-lg border border-destructive/40 p-3"
        role="alert"
        data-testid="invoice-from-pdf-create-quota"
      >
        <p className="min-w-0 text-sm">
          <span className="font-medium text-destructive">
            {t('invoiceFromPdf.create.quotaTitle')}
          </span>{' '}
          <span className="text-muted-foreground tabular-nums">
            {t('invoiceFromPdf.quotaExceeded.body', {
              used: failure.quota.current,
              limit: failure.quota.limit,
            })}
          </span>
        </p>
        <Link
          to="/subscription"
          className={buttonVariants({ variant: 'outline', size: 'sm', className: 'ml-auto' })}
        >
          {t('invoiceFromPdf.quotaExceeded.cta')}
        </Link>
      </div>
    )
  }
  if (failure.kind === 'compliance') {
    return (
      <div
        className="space-y-1 rounded-lg border border-destructive/40 p-3 text-sm"
        role="alert"
        data-testid="invoice-from-pdf-create-errors"
      >
        <p className="font-medium text-destructive">{t('invoiceFromPdf.create.errorsTitle')}</p>
        <ul className="list-inside list-disc">
          {failure.errors.map((e) => (
            <li key={`${e.code ?? ''}-${e.field ?? ''}-${e.message}`}>
              {e.code ? `${e.code}: ` : ''}
              {e.message}
              {e.field ? ` (${e.field})` : ''}
            </li>
          ))}
        </ul>
      </div>
    )
  }
  const message =
    failure.kind === 'failed' && failure.message
      ? failure.message
      : t(`invoiceFromPdf.errors.${failure.kind}`)
  return (
    <p
      className="rounded-lg border border-destructive/40 p-3 text-sm text-destructive"
      role="alert"
      data-testid="invoice-from-pdf-create-failed"
    >
      {message}
    </p>
  )
}

export interface CreateStepProps {
  output: OutputKind
  onOutputChange: (output: OutputKind) => void
  templateId: string | null
  onTemplateChange: (templateId: string | null) => void
  savedTemplates: Array<{ id: string; name: string }>
  result: CreatedDocument | null
  failure: CreateFailure | null
  running: boolean
  onCreate: () => void
  onBack: () => void
}

export function CreateStep({
  output,
  onOutputChange,
  templateId,
  onTemplateChange,
  savedTemplates,
  result,
  failure,
  running,
  onCreate,
  onBack,
}: CreateStepProps) {
  const { t } = useTranslation()
  const [showXml, setShowXml] = useState(false)
  const xml = result
    ? (result.embeddedXml ?? (result.mimeType.includes('xml') ? decodeXml(result.data) : null))
    : null

  return (
    <section className="space-y-4 rounded-xl border bg-card p-6 shadow-sm">
      {result && <Verdict doc={result} />}

      <fieldset className="space-y-2" data-testid="invoice-from-pdf-outputs">
        <legend className="sr-only">{t('invoiceFromPdf.create.outputLegend')}</legend>
        {OUTPUT_KINDS.map((kind) => {
          const checked = output === kind
          return (
            <label
              key={kind}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-[background-color,border-color] motion-reduce:transition-none has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-ring ${
                checked ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
              }`}
              data-testid={`invoice-from-pdf-output-${kind}`}
            >
              <input
                type="radio"
                name="invoice-from-pdf-output"
                value={kind}
                checked={checked}
                onChange={() => {
                  setShowXml(false)
                  onOutputChange(kind)
                }}
                className="mt-1 accent-[var(--primary)]"
              />
              <span className="min-w-0">
                <span className="font-medium">
                  {t(`invoiceFromPdf.create.output.${kind}.title`)}
                </span>
                {kind === 'embed' && (
                  <span className="text-muted-foreground">
                    {' · '}
                    {t('invoiceFromPdf.create.recommended')}
                  </span>
                )}
                <span className="block text-sm text-muted-foreground">
                  {t(`invoiceFromPdf.create.output.${kind}.body`)}
                </span>
              </span>
            </label>
          )
        })}
      </fieldset>

      {output === 'generate' && savedTemplates.length > 0 && (
        <div className="grid grid-cols-12 items-center gap-3">
          <span className="col-span-12 text-sm text-muted-foreground sm:col-span-3">
            {t('invoiceFromPdf.create.template')}
          </span>
          <div className="col-span-12 sm:col-span-6">
            <Select
              value={templateId ?? DEFAULT_TEMPLATE}
              onValueChange={(v) =>
                onTemplateChange(typeof v === 'string' && v !== DEFAULT_TEMPLATE ? v : null)
              }
            >
              <SelectTrigger
                className="h-9 w-full"
                aria-label={t('invoiceFromPdf.create.template')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DEFAULT_TEMPLATE}>
                  {t('invoiceFromPdf.create.defaultTemplate')}
                </SelectItem>
                {savedTemplates.map((tpl) => (
                  <SelectItem key={tpl.id} value={tpl.id}>
                    {tpl.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {result?.carrier?.pdfA3Declared && (
        <p
          className="rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100"
          data-testid="invoice-from-pdf-pdfa-note"
        >
          {t('invoiceFromPdf.create.pdfA3Note')}
        </p>
      )}
      {(result?.carrier?.warnings ?? []).length > 0 && (
        <ul className="list-inside list-disc text-sm text-muted-foreground">
          {(result?.carrier?.warnings ?? []).map((w) => (
            <li key={`${w.code ?? ''}-${w.message}`}>{w.message}</li>
          ))}
        </ul>
      )}

      {failure && <FailureBlock failure={failure} />}

      {showXml && xml && (
        <pre
          className="max-h-[60dvh] overflow-auto rounded-lg border bg-muted p-3 font-mono text-xs"
          data-testid="invoice-from-pdf-xml"
        >
          {xml}
        </pre>
      )}

      <div className="flex flex-wrap justify-end gap-3">
        <Button variant="outline" onClick={onBack} className="mr-auto">
          {t('invoiceFromPdf.create.back')}
        </Button>
        {result && xml && (
          <Button variant="outline" onClick={() => setShowXml((v) => !v)} aria-expanded={showXml}>
            {t(showXml ? 'invoiceFromPdf.create.hideXml' : 'invoiceFromPdf.create.viewXml')}
          </Button>
        )}
        {result ? (
          <Button onClick={() => download(result)} data-testid="invoice-from-pdf-download">
            {t('invoiceFromPdf.create.download')}
          </Button>
        ) : (
          <Button onClick={onCreate} disabled={running} data-testid="invoice-from-pdf-create">
            {running ? (
              <>
                <RefreshCw
                  className="h-4 w-4 animate-spin motion-reduce:animate-none"
                  aria-hidden="true"
                />
                {t('invoiceFromPdf.create.running')}
              </>
            ) : (
              t('invoiceFromPdf.create.run')
            )}
          </Button>
        )}
      </div>
    </section>
  )
}
